import Button from 'ext-ui/components/Button/Button'
import Stepper from 'ext-ui/components/Stepper/Stepper'
import React, { useEffect } from 'react'
import { useHistory } from 'react-router'
import { WizardStepId } from 'src/app/BulkInvite/bulkInvite.types'
import BulkInviteSendingToastMessage from 'src/app/BulkInvite/BulkInviteSendingToastMessage'
import * as style from 'src/app/BulkInvite/BulkInviteWizard.style'
import Heading from 'src/app/BulkInvite/components/Heading'
import { useBulkInvite } from 'src/app/BulkInvite/context/BulkInviteContext'
import MappingStep from 'src/app/BulkInvite/steps/MappingStep'
import ReviewStep from 'src/app/BulkInvite/steps/ReviewStep'
import UploadStep from 'src/app/BulkInvite/steps/UploadStep'
import { useBulkInviteStrings } from 'src/app/BulkInvite/useBulkInviteStrings'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { useBackgroundDispatch } from 'src/hooks'
import { peopleInvitedToOrgUrl } from 'src/navigation/urls'

const STEP_ORDER: WizardStepId[] = ['upload', 'mapping', 'review']

const STEP_COMPONENTS: Record<WizardStepId, React.FC> = {
  upload: UploadStep,
  mapping: MappingStep,
  review: ReviewStep,
}

interface FooterConfig {
  hide?: boolean
  showBack?: boolean
  primaryLabel?: string
  primaryAction?: () => void
  primaryDisabled?: boolean
  primaryLoading?: boolean
}

interface BulkInviteWizardProps {
  /** Closes the parent modal once a send succeeds. */
  onClose: () => void
}

const BulkInviteWizard: React.FC<BulkInviteWizardProps> = ({ onClose }) => {
  const {
    step,
    goToStep,
    back,
    isMappingComplete,
    savingMapping,
    submitMapping,
    preview,
    invitedCount,
    sending,
    send,
    submitted,
  } = useBulkInvite()
  const strings = useBulkInviteStrings()
  const dispatch = useBackgroundDispatch()
  const history = useHistory()

  // On a successful send the context flips `submitted`. Navigation, the modal
  // close, and the async toast all belong at this layer, so handle them here
  // rather than inside the context.
  useEffect(() => {
    if (!submitted) return
    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: <BulkInviteSendingToastMessage count={invitedCount} />,
        inProgressColorScheme: true,
        hideImage: true,
      }),
    )
    onClose()
    history.push(`${peopleInvitedToOrgUrl()}?showDeactivatedUsers=true`)
  }, [submitted, invitedCount, dispatch, history, onClose])

  const stepTitles: Record<WizardStepId, string> = {
    upload: strings.stepUpload,
    mapping: strings.stepMapping,
    review: strings.stepReview,
  }

  const currentStep = STEP_ORDER.indexOf(step) + 1
  const ActiveStep = STEP_COMPONENTS[step]

  const footer = ((): FooterConfig => {
    switch (step) {
      case 'upload':
        return { showBack: false }
      case 'mapping':
        return {
          showBack: true,
          primaryLabel: strings.mappingContinueCta,
          primaryAction: submitMapping,
          primaryDisabled: !isMappingComplete || savingMapping,
          primaryLoading: savingMapping,
        }
      case 'review':
        return {
          showBack: true,
          primaryLabel: strings.reviewConfirmSend(invitedCount),
          primaryAction: send,
          primaryLoading: sending,
          primaryDisabled: !preview || invitedCount === 0 || sending,
        }
      default:
        return { hide: true }
    }
  })()

  return (
    <div css={style.root}>
      <header css={style.header}>
        <Heading variant="section" ariaLevel={1}>
          {strings.shellTitle}
        </Heading>
        <p css={style.headerSubtitle}>{strings.shellSubtitle}</p>
      </header>

      <Stepper
        steps={STEP_ORDER.map((id) => ({
          title: stepTitles[id],
          testId: `bulkInviteStep-${id}`,
        }))}
        currentStep={currentStep}
        onStepSelect={(n): void => goToStep(STEP_ORDER[n - 1])}
        ariaLabel={strings.stepperAriaLabel}
        getStepNavLabel={strings.goBackTo}
        orientation="horizontal"
      />

      <div css={style.body}>
        <section css={style.main}>
          <div css={style.content}>
            <ActiveStep />
          </div>

          {!footer.hide && (footer.showBack || footer.primaryLabel) && (
            <footer css={style.footer}>
              {footer.showBack ? (
                <Button
                  variant="secondary"
                  onClick={back}
                  testId="bulkInviteBack"
                >
                  {strings.back}
                </Button>
              ) : (
                <span />
              )}
              {footer.primaryLabel && footer.primaryAction ? (
                <Button
                  variant="primary"
                  onClick={footer.primaryAction}
                  disabled={footer.primaryDisabled}
                  loading={footer.primaryLoading}
                  testId="bulkInvitePrimary"
                >
                  {footer.primaryLabel}
                </Button>
              ) : (
                <span />
              )}
            </footer>
          )}
        </section>
      </div>
    </div>
  )
}

export default BulkInviteWizard
